import React, { useMemo, useState } from 'react';
import { Customer, KotLine, Product, TableOrder, Transaction, TransactionItem } from '../types';
import { usePos } from '../context/PosContext';
import { useRestaurant } from '../hooks/useRestaurant';
import { printKotRouted } from '../services/kotRouting';
import { printCouponSlip, printReceiptAuto } from '../services/receipt';
import PaymentModal from './PaymentModal';
import '../pages/Restaurant.css';

interface DraftLine {
  productId: string;
  name: string;
  unitPrice: number;
  quantity: number;
  remark?: string;
}

interface Props {
  tableName: string;
  rest: ReturnType<typeof useRestaurant>;
  // 'cashier' shows the Settle action; 'captain' only fires KOTs.
  mode: 'cashier' | 'captain';
  onClose: () => void;
}

const OrderPanel: React.FC<Props> = ({ tableName, rest, mode, onClose }) => {
  const { products, customers, formatMoney, isOn, config, recordSale } = usePos();
  const order = rest.runningOrder(tableName);

  const [waiter, setWaiter] = useState(order?.waiter ?? '');
  const [customerId, setCustomerId] = useState<string | undefined>(order?.customerId);
  const [customerName, setCustomerName] = useState(order?.customerName ?? '');
  const [covers, setCovers] = useState<string>(order?.covers ? String(order.covers) : '');
  const [discInput, setDiscInput] = useState<string>(
    order?.discountAmount ? String(order.discountAmount) : ''
  );
  const [discMode, setDiscMode] = useState<'amt' | 'pct'>('amt');

  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [pendingTx, setPendingTx] = useState<Transaction | null>(null);
  const [custSearch, setCustSearch] = useState('');
  // "Table Actions" menu + sub-panels (cashier only): swap / transfer / merge /
  // move items / bill merge / split bill / print bill / void.
  type TableAction = 'swap' | 'transfer' | 'merge' | 'split' | 'billmerge' | 'splitbill' | 'equalsplit';
  const [menuOpen, setMenuOpen] = useState(false);
  const [tableAction, setTableAction] = useState<TableAction | null>(null);
  const [targetTable, setTargetTable] = useState('');
  const [moveQty, setMoveQty] = useState<Record<string, string>>({});
  const [billTables, setBillTables] = useState<string[]>([]);
  // What the open PaymentModal is settling, so onSettled finalizes correctly.
  const [pendingMode, setPendingMode] = useState<'table' | 'merged' | 'split'>('table');
  // Equal split in progress: paying share `k` of `n`. Each share is a split
  // bill of 1/n of every line, so it flows through finalizeSplitSettle.
  const [equalWays, setEqualWays] = useState('2');
  const [equalRun, setEqualRun] = useState<{ k: number; n: number } | null>(null);

  const askCaptain = isOn('ask_captain_details') || mode === 'captain';

  const results = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    return products
      .filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.barcode?.includes(search) ||
          p.category?.toLowerCase().includes(term)
      )
      .slice(0, 8);
  }, [search, products]);

  const custResults = useMemo(() => {
    const term = custSearch.trim().toLowerCase();
    if (!term) return [];
    return customers
      .filter((c) => c.name.toLowerCase().includes(term) || c.phone?.includes(custSearch))
      .slice(0, 6);
  }, [custSearch, customers]);

  const selectedCustomer: Customer | undefined = customers.find((c) => c.id === customerId);

  const addProduct = (p: Product) => {
    setDraft((prev) => {
      const match = prev.find((d) => d.productId === p.id);
      if (match) {
        return prev.map((d) => (d.productId === p.id ? { ...d, quantity: d.quantity + 1 } : d));
      }
      return [...prev, { productId: p.id, name: p.name, unitPrice: p.price, quantity: 1 }];
    });
    setSearch('');
  };

  const bumpDraft = (id: string, delta: number) =>
    setDraft((prev) =>
      prev
        .map((d) => (d.productId === id ? { ...d, quantity: d.quantity + delta } : d))
        .filter((d) => d.quantity > 0)
    );

  const removeDraft = (id: string) => setDraft((prev) => prev.filter((d) => d.productId !== id));

  const setDraftRemark = (id: string, remark: string) =>
    setDraft((prev) => prev.map((d) => (d.productId === id ? { ...d, remark } : d)));

  const pickCustomer = (c: Customer) => {
    setCustomerId(c.id);
    setCustomerName(c.name);
    setCustSearch('');
  };
  const clearCustomer = () => {
    setCustomerId(undefined);
    setCustomerName('');
  };

  const draftTotal = draft.reduce((s, d) => s + d.unitPrice * d.quantity, 0);
  const sentSubtotal = order?.items.reduce((s, i) => s + i.total, 0) ?? 0;
  const sentTax = order?.items.reduce((s, i) => s + i.total * (i.taxRatePercent / 100), 0) ?? 0;
  const grossTotal = sentSubtotal + sentTax + draftTotal;

  // Resolve the discount input (amount or percent of the sent subtotal+tax).
  const discountAmount = useMemo(() => {
    const n = parseFloat(discInput) || 0;
    if (n <= 0) return 0;
    return discMode === 'pct' ? ((sentSubtotal + sentTax) * n) / 100 : n;
  }, [discInput, discMode, sentSubtotal, sentTax]);

  const netTotal = Math.max(0, grossTotal - discountAmount);

  const coversNum = covers ? parseInt(covers, 10) : undefined;

  const persistMeta = () =>
    rest.setOrderMeta(tableName, {
      waiter: waiter || undefined,
      customerId,
      customerName: customerName || undefined,
      covers: coversNum,
      discountAmount,
    });

  const sendKot = async () => {
    const lines: KotLine[] = draft.map((d) => ({
      productId: d.productId,
      name: d.name,
      quantity: d.quantity,
      unitPrice: d.unitPrice,
      remark: d.remark?.trim() || undefined,
    }));
    // Header meta is written onto the order atomically by sendKot, so it
    // persists even on the very first KOT (when no order existed yet).
    const kot = await rest.sendKot(tableName, lines, {
      waiter: waiter || undefined,
      customerName: customerName || undefined,
      customerId,
      covers: coversNum,
      discountAmount,
    });
    setDraft([]);
    // "17 — Captain App / B. Disable Print Button": captains fire KOTs without
    // printing them (the ticket is still saved for the KOT Report).
    if (mode === 'captain' && isOn('captain_disable_print_button')) return;
    // Fire the ticket to the kitchen, routed per service point: lines whose
    // product belongs to a station with an assigned USB printer print there,
    // the rest go to the default printer (silent no-op without USB — the KOT
    // is still saved and visible in the KOT Report).
    if (kot)
      printKotRouted(kot, config.storeName, (pid) => products.find((p) => p.id === pid)?.servicePoint).catch((e) =>
        console.warn('KOT print failed:', e)
      );
  };

  // Open the shared PaymentModal (split tender / loyalty) on a draft sale.
  const openSettle = async () => {
    persistMeta();
    const draftTx = await rest.buildSettleDraft(tableName, {
      discountAmount,
      covers: coversNum,
    });
    if (draftTx) {
      setPendingMode('table');
      setPendingTx(draftTx);
    }
  };

  // PaymentModal records the sale; we then mark the table(s) settled + KOTs
  // billed — or, for a split bill, just pull the paid items off the table.
  const onSettled = async (tx: Transaction): Promise<Transaction> => {
    const finalized = await recordSale(tx);
    if (pendingMode === 'merged') rest.finalizeSettleMany([tableName, ...billTables], finalized);
    else if (pendingMode === 'split') rest.finalizeSplitSettle(tableName, finalized);
    else rest.finalizeSettle(tableName, finalized);
    // "15 — Table Settings / F - Table Coupon": hand the table a coupon slip.
    if (isOn('table_coupon')) printCouponSlip(finalized, config);
    setPendingTx(null);
    setBillTables([]);
    setMoveQty({});
    // Equal split: chain straight into the next share's payment. The share is
    // computed from what finalizeSplitSettle just left on the table.
    if (equalRun && equalRun.k < equalRun.n) {
      const next = { k: equalRun.k + 1, n: equalRun.n };
      const leftover = removeShare(order, finalized);
      const picks = leftover.map((i) => ({
        productId: i.productId,
        unitPrice: i.unitPrice,
        quantity: next.k === next.n ? i.quantity : Math.round((i.quantity / (next.n - next.k + 1)) * 1000) / 1000,
      }));
      const draftTx = await rest.buildSplitSettleDraft(tableName, picks, {
        discountAmount: shareDiscount(next.k, next.n),
        label: `(${next.k}/${next.n})`,
      });
      if (draftTx) {
        setEqualRun(next);
        setPendingTx(draftTx);
        return finalized;
      }
    }
    setEqualRun(null);
    setTableAction(null);
    // A split bill that leaves items unpaid keeps the panel open on the rest.
    const paidQty = finalized.items.reduce((s, i) => s + i.quantity, 0);
    const tableQty = order?.items.reduce((s, i) => s + i.quantity, 0) ?? 0;
    if (!(pendingMode === 'split' && paidQty < tableQty - 0.0005)) onClose();
    return finalized;
  };

  // Discount is spread across the equal shares (last share takes the remainder).
  const shareDiscount = (k: number, n: number) => {
    const per = Math.round((discountAmount / n) * 100) / 100;
    return k === n ? Math.round((discountAmount - per * (n - 1)) * 100) / 100 : per;
  };

  // Equal split: start with share 1 of N; onSettled chains the rest.
  const startEqualSplit = async () => {
    const n = parseInt(equalWays, 10) || 0;
    if (n < 2 || !order || order.items.length === 0) return;
    persistMeta();
    const draftTx = await rest.buildSplitSettleDraft(tableName, rest.equalSharePicks(tableName, 1, n), {
      discountAmount: shareDiscount(1, n),
      label: `(1/${n})`,
    });
    if (draftTx) {
      setEqualRun({ k: 1, n });
      setPendingMode('split');
      setPendingTx(draftTx);
    }
  };

  const otherTables = rest.tables.filter((t) => t !== tableName);
  const isOccupied = (t: string) => !!rest.runningOrder(t);
  const hasItems = (t: string) => (rest.runningOrder(t)?.items.length ?? 0) > 0;

  const openAction = (a: TableAction) => {
    setMenuOpen(false);
    setTableAction(a);
    setTargetTable('');
    setMoveQty({});
    setBillTables([]);
  };

  // Another terminal editing the target table must not have its order yanked
  // out from under it.
  const targetLocked = (t: string): boolean => {
    const holder = rest.lockHolder(t);
    if (!holder) return false;
    window.alert(`${t} is being edited on ${holder.deviceName || 'another terminal'}.`);
    return true;
  };

  const doMerge = () => {
    if (!targetTable || targetLocked(targetTable)) return;
    rest.mergeTables(tableName, targetTable);
    setTableAction(null);
    onClose(); // this table is now freed / relocated
  };

  const doSwap = () => {
    if (!targetTable || targetLocked(targetTable)) return;
    rest.swapTables(tableName, targetTable);
    setTableAction(null);
    onClose(); // this table now holds the other order (or is free)
  };

  const doTransfer = () => {
    if (!targetTable || targetLocked(targetTable)) return;
    if (!rest.transferTable(tableName, targetTable)) {
      window.alert(`${targetTable} is occupied — use Merge or Swap instead.`);
      return;
    }
    setTableAction(null);
    onClose();
  };

  const doVoid = () => {
    setMenuOpen(false);
    if (!order) return;
    if (!window.confirm(`Void Table ${tableName}? All active KOTs will be cancelled and the table freed.`)) return;
    rest.voidTable(tableName);
    onClose();
  };

  // Print a pre-bill (running total) without settling, so guests can check it.
  const printBill = async () => {
    setMenuOpen(false);
    persistMeta();
    const draftTx = await rest.buildSettleDraft(tableName, { discountAmount, covers: coversNum });
    if (draftTx) printReceiptAuto(draftTx, config).catch((e) => console.warn('Print failed:', e));
  };

  // One bill across this table + the ticked ones.
  const doBillMerge = async () => {
    if (billTables.length === 0 || billTables.some(targetLocked)) return;
    persistMeta();
    const draftTx = await rest.buildMergedSettleDraft([tableName, ...billTables]);
    if (draftTx) {
      setPendingMode('merged');
      setPendingTx(draftTx);
    }
  };

  // Bill only the picked quantities now; the rest stays on the table.
  const doSplitBill = async () => {
    const picks = (order?.items ?? [])
      .map((i) => ({ productId: i.productId, unitPrice: i.unitPrice, quantity: parseInt(moveQty[`${i.productId}-${i.unitPrice}`] || '0', 10) || 0 }))
      .filter((m) => m.quantity > 0);
    if (picks.length === 0) return;
    const draftTx = await rest.buildSplitSettleDraft(tableName, picks);
    if (draftTx) {
      setPendingMode('split');
      setPendingTx(draftTx);
    }
  };

  const doSplit = () => {
    if (!targetTable) return;
    const moves = (order?.items ?? [])
      .map((i) => ({ productId: i.productId, unitPrice: i.unitPrice, quantity: parseInt(moveQty[`${i.productId}-${i.unitPrice}`] || '0', 10) || 0 }))
      .filter((m) => m.quantity > 0);
    if (moves.length === 0) return;
    rest.splitTable(tableName, targetTable, moves);
    setTableAction(null);
    setMoveQty({});
    setTargetTable('');
  };

  // "15 — Table Settings / K1..K9" (cashier) and "17 — Captain App / D1..D9"
  // (captain): each Table Action can be switched off individually. Actions
  // that need fired items are hidden on a booked-only table.
  const hasSent = (order?.items.length ?? 0) > 0;
  const actionsMaster = mode === 'captain' ? 'captain_table_actions' : 'table_extra_actions';
  const actionKey = (k: string) => (mode === 'captain' ? 'captain_action_' : 'table_action_') + k;
  const menuItems = (
    [
      { key: 'swap', label: 'Swap Table', onClick: () => openAction('swap') },
      { key: 'transfer', label: 'Transfer to Free Table', onClick: () => openAction('transfer') },
      { key: 'merge', label: 'Merge into Table', onClick: () => openAction('merge') },
      { key: 'move_items', label: 'Move Items to Table', onClick: () => openAction('split'), needsItems: true },
      { sep: true, group: 'bill' },
      { key: 'bill_merge', label: 'Bill Merge (pay together)', onClick: () => openAction('billmerge'), needsItems: true },
      { key: 'split_bill', label: 'Split Bill (pay part)', onClick: () => openAction('splitbill'), needsItems: true },
      { key: 'equal_split', label: 'Equal Split (N bills)', onClick: () => openAction('equalsplit'), needsItems: true },
      { key: 'print_bill', label: 'Print Bill (pre-bill)', onClick: printBill, needsItems: true },
      { sep: true, group: 'void' },
      { key: 'void', label: 'Void Table', onClick: doVoid, danger: true },
    ] as {
      key?: string;
      label?: string;
      onClick?: () => void;
      needsItems?: boolean;
      danger?: boolean;
      sep?: boolean;
      group?: string;
    }[]
  )
    .filter((m) => m.sep || (isOn(actionKey(m.key!)) && (!m.needsItems || hasSent)))
    // Drop separators that would sit at the edges or next to another separator.
    .filter((m, i, arr) => !m.sep || (i > 0 && i < arr.length - 1 && !arr[i - 1].sep))
    .filter((m, i, arr) => !m.sep || i < arr.length - 1);

  return (
    <div className="ord-overlay" onClick={onClose}>
      <div className="ord-panel" onClick={(e) => e.stopPropagation()}>
        <div className="ord-head">
          <h2>Table {tableName}</h2>
          <div className="ord-head-actions">
            {order && isOn(actionsMaster) && menuItems.length > 0 && (
              <div className="ord-menu-wrap">
                <button className="ord-mini-btn" onClick={() => setMenuOpen((v) => !v)}>
                  Table Actions ▾
                </button>
                {menuOpen && (
                  <ul className="ord-menu" onMouseLeave={() => setMenuOpen(false)}>
                    {menuItems.map((m, idx) =>
                      m.sep ? (
                        <li className="sep" key={`sep-${idx}`} />
                      ) : (
                        <li key={m.label} className={m.danger ? 'danger' : undefined} onClick={m.onClick}>
                          {m.label}
                        </li>
                      )
                    )}
                  </ul>
                )}
              </div>
            )}
            <button className="ord-close" onClick={onClose}>×</button>
          </div>
        </div>

        {tableAction === 'swap' && (
          <div className="ord-tableaction">
            <div className="ota-row">
              <span>Swap Table {tableName} with</span>
              <select value={targetTable} onChange={(e) => setTargetTable(e.target.value)}>
                <option value="">Select table…</option>
                {otherTables.filter(isOccupied).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="ota-hint">Both tables exchange their running orders and KOTs.</div>
            <div className="ota-actions">
              <button className="btn-kot" onClick={() => setTableAction(null)}>Cancel</button>
              <button className="btn-settle" onClick={doSwap} disabled={!targetTable}>Swap</button>
            </div>
          </div>
        )}

        {tableAction === 'transfer' && (
          <div className="ord-tableaction">
            <div className="ota-row">
              <span>Transfer Table {tableName} to</span>
              <select value={targetTable} onChange={(e) => setTargetTable(e.target.value)}>
                <option value="">Select free table…</option>
                {otherTables.filter((t) => !isOccupied(t)).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="ota-hint">Moves the whole order and its KOTs; this table becomes free.</div>
            <div className="ota-actions">
              <button className="btn-kot" onClick={() => setTableAction(null)}>Cancel</button>
              <button className="btn-settle" onClick={doTransfer} disabled={!targetTable}>Transfer</button>
            </div>
          </div>
        )}

        {tableAction === 'billmerge' && (
          <div className="ord-tableaction">
            <div className="ota-row"><span>Bill Table {tableName} together with</span></div>
            <div className="ota-items">
              {otherTables.filter(hasItems).map((t) => {
                const o = rest.runningOrder(t)!;
                const tot = o.items.reduce((s, i) => s + i.total, 0);
                return (
                  <label className="ota-item ota-check" key={t}>
                    <input
                      type="checkbox"
                      checked={billTables.includes(t)}
                      onChange={(e) =>
                        setBillTables((p) => (e.target.checked ? [...p, t] : p.filter((x) => x !== t)))
                      }
                    />
                    <span className="ota-name">{t}</span>
                    <span className="ota-have">{o.items.length} lines · {formatMoney(tot)}</span>
                  </label>
                );
              })}
              {otherTables.filter(hasItems).length === 0 && (
                <div className="ota-hint">No other occupied tables to bill with.</div>
              )}
            </div>
            <div className="ota-hint">One bill covers all selected tables; they are all freed once paid.</div>
            <div className="ota-actions">
              <button className="btn-kot" onClick={() => setTableAction(null)}>Cancel</button>
              <button className="btn-settle" onClick={doBillMerge} disabled={billTables.length === 0}>
                Bill Together
              </button>
            </div>
          </div>
        )}

        {tableAction === 'splitbill' && (
          <div className="ord-tableaction">
            <div className="ota-row">
              <span>Quantity to bill now</span>
              <button
                className="ota-link"
                onClick={() =>
                  setMoveQty(
                    Object.fromEntries((order?.items ?? []).map((i) => [`${i.productId}-${i.unitPrice}`, String(i.quantity)]))
                  )
                }
              >
                Select all
              </button>
            </div>
            <div className="ota-items">
              {(order?.items ?? []).map((i) => {
                const key = `${i.productId}-${i.unitPrice}`;
                return (
                  <div className="ota-item" key={key}>
                    <span className="ota-name">{i.name}</span>
                    <span className="ota-have">of {i.quantity}</span>
                    <input
                      type="number"
                      min={0}
                      max={i.quantity}
                      placeholder="0"
                      value={moveQty[key] ?? ''}
                      onChange={(e) => setMoveQty((p) => ({ ...p, [key]: e.target.value }))}
                    />
                  </div>
                );
              })}
            </div>
            <div className="ota-hint">
              Unpaid items stay on the table. To split one bill across cash / card, use Split tender at payment.
            </div>
            <div className="ota-actions">
              <button className="btn-kot" onClick={() => setTableAction(null)}>Cancel</button>
              <button
                className="btn-settle"
                onClick={doSplitBill}
                disabled={!Object.values(moveQty).some((v) => (parseInt(v || '0', 10) || 0) > 0)}
              >
                Bill Selected
              </button>
            </div>
          </div>
        )}

        {tableAction === 'equalsplit' && (
          <div className="ord-tableaction">
            <div className="ota-row">
              <span>Split Table {tableName} equally into</span>
              <input
                type="number"
                min={2}
                max={20}
                value={equalWays}
                onChange={(e) => setEqualWays(e.target.value)}
                style={{ width: 70, padding: 8, border: '1px solid #e0c98a', borderRadius: 6 }}
              />
              <span>bills</span>
            </div>
            <div className="ota-hint">
              {(parseInt(equalWays, 10) || 0) >= 2
                ? `Each bill is about ${formatMoney(netTotal / (parseInt(equalWays, 10) || 1))}: every line is billed at 1/${equalWays} of its quantity, paid one after another.`
                : 'Enter 2 or more.'}
            </div>
            <div className="ota-actions">
              <button className="btn-kot" onClick={() => setTableAction(null)}>Cancel</button>
              <button
                className="btn-settle"
                onClick={startEqualSplit}
                disabled={(parseInt(equalWays, 10) || 0) < 2 || draft.length > 0}
                title={draft.length > 0 ? 'Send the pending KOT first' : undefined}
              >
                Start Split
              </button>
            </div>
          </div>
        )}

        {tableAction === 'merge' && (
          <div className="ord-tableaction">
            <div className="ota-row">
              <span>Merge Table {tableName} into</span>
              <select value={targetTable} onChange={(e) => setTargetTable(e.target.value)}>
                <option value="">Select table…</option>
                {otherTables.map((t) => (
                  <option key={t} value={t}>
                    {t} {isOccupied(t) ? '(occupied)' : '(free)'}
                  </option>
                ))}
              </select>
            </div>
            <div className="ota-actions">
              <button className="btn-kot" onClick={() => setTableAction(null)}>Cancel</button>
              <button className="btn-settle" onClick={doMerge} disabled={!targetTable}>Merge</button>
            </div>
          </div>
        )}

        {tableAction === 'split' && (
          <div className="ord-tableaction">
            <div className="ota-row">
              <span>Move items from {tableName} to</span>
              <select value={targetTable} onChange={(e) => setTargetTable(e.target.value)}>
                <option value="">Select table…</option>
                {otherTables.map((t) => (
                  <option key={t} value={t}>
                    {t} {isOccupied(t) ? '(occupied)' : '(free)'}
                  </option>
                ))}
              </select>
            </div>
            <div className="ota-items">
              {(order?.items ?? []).map((i) => {
                const key = `${i.productId}-${i.unitPrice}`;
                return (
                  <div className="ota-item" key={key}>
                    <span className="ota-name">{i.name}</span>
                    <span className="ota-have">of {i.quantity}</span>
                    <input
                      type="number"
                      min={0}
                      max={i.quantity}
                      placeholder="0"
                      value={moveQty[key] ?? ''}
                      onChange={(e) => setMoveQty((p) => ({ ...p, [key]: e.target.value }))}
                    />
                  </div>
                );
              })}
            </div>
            <div className="ota-actions">
              <button className="btn-kot" onClick={() => setTableAction(null)}>Cancel</button>
              <button className="btn-settle" onClick={doSplit} disabled={!targetTable}>Move Items</button>
            </div>
          </div>
        )}

        <div className="ord-body">
          <div className="ord-meta-row">
            {askCaptain && (
              <input
                placeholder="Waiter / Captain"
                value={waiter}
                onChange={(e) => setWaiter(e.target.value)}
              />
            )}
            <input
              type="number"
              min={0}
              placeholder="Covers"
              value={covers}
              onChange={(e) => setCovers(e.target.value)}
              style={{ maxWidth: 90 }}
            />
          </div>

          {/* Customer link */}
          <div className="ord-search">
            {selectedCustomer || customerName ? (
              <div className="ord-customer">
                <div>
                  <strong>{customerName}</strong>
                  {selectedCustomer?.phone && <span className="muted"> · {selectedCustomer.phone}</span>}
                  {selectedCustomer && (selectedCustomer.creditBalance ?? 0) > 0 && (
                    <div className="ord-balance">
                      Balance due: {formatMoney(selectedCustomer.creditBalance ?? 0)}
                    </div>
                  )}
                </div>
                <button className="ol-del" onClick={clearCustomer}>
                  ×
                </button>
              </div>
            ) : (
              <>
                <input
                  placeholder="Link customer by name / mobile (optional)…"
                  value={custSearch}
                  onChange={(e) => setCustSearch(e.target.value)}
                />
                {custResults.length > 0 && (
                  <ul className="ord-results">
                    {custResults.map((c) => (
                      <li key={c.id} onClick={() => pickCustomer(c)}>
                        <span>{c.name}</span>
                        <span className="muted">{c.phone}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {/* Product search */}
          <div className="ord-search">
            <input
              placeholder="Search product by name / code / barcode…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            {results.length > 0 && (
              <ul className="ord-results">
                {results.map((p) => (
                  <li key={p.id} onClick={() => addProduct(p)}>
                    <span>{p.name}</span>
                    <span className="pr-price">{formatMoney(p.price)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Already-fired items (editable) */}
          {order && order.items.length > 0 && (
            <div className="ord-lines">
              {order.items.map((i) => (
                <div className="ord-line sent" key={`sent-${i.productId}-${i.unitPrice}`}>
                  <span className="ol-name">
                    {i.name}
                    <span className="ol-tag">SENT</span>
                  </span>
                  <span className="ol-qty">
                    <button
                      onClick={() =>
                        rest.updateOrderItemQty(tableName, i.productId, i.unitPrice, i.quantity - 1)
                      }
                    >
                      −
                    </button>
                    {i.quantity}
                    <button
                      onClick={() =>
                        rest.updateOrderItemQty(tableName, i.productId, i.unitPrice, i.quantity + 1)
                      }
                    >
                      +
                    </button>
                  </span>
                  <span className="ol-total">{formatMoney(i.total)}</span>
                  <button
                    className="ol-del"
                    title="Void this line"
                    onClick={() => rest.updateOrderItemQty(tableName, i.productId, i.unitPrice, 0)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* New (unsent) draft items with per-line KOT remark */}
          {draft.length > 0 && (
            <div className="ord-lines">
              {draft.map((d) => (
                <div className="ord-draft" key={`draft-${d.productId}`}>
                  <div className="ord-line">
                    <span className="ol-name">{d.name}</span>
                    <span className="ol-qty">
                      <button onClick={() => bumpDraft(d.productId, -1)}>−</button>
                      {d.quantity}
                      <button onClick={() => bumpDraft(d.productId, 1)}>+</button>
                    </span>
                    <span className="ol-total">{formatMoney(d.unitPrice * d.quantity)}</span>
                    <button className="ol-del" onClick={() => removeDraft(d.productId)}>
                      ×
                    </button>
                  </div>
                  {isOn('kot_remark') && (
                    <input
                      className="ol-remark"
                      placeholder="KOT remark (e.g. less spicy)…"
                      value={d.remark ?? ''}
                      onChange={(e) => setDraftRemark(d.productId, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {!order && draft.length === 0 && (
            <p className="empty-hint">Search and add items, then send a KOT to the kitchen.</p>
          )}
        </div>

        <div className="ord-foot">
          {/* Discount (cashier only) */}
          {mode === 'cashier' && grossTotal > 0 && (
            <div className="ord-discount">
              <span>Discount</span>
              <input
                type="number"
                min={0}
                placeholder="0"
                value={discInput}
                onChange={(e) => setDiscInput(e.target.value)}
              />
              <div className="ord-disc-toggle">
                <button
                  className={discMode === 'amt' ? 'active' : ''}
                  onClick={() => setDiscMode('amt')}
                >
                  ₹
                </button>
                <button
                  className={discMode === 'pct' ? 'active' : ''}
                  onClick={() => setDiscMode('pct')}
                >
                  %
                </button>
              </div>
              <span className="ord-disc-val">− {formatMoney(discountAmount)}</span>
            </div>
          )}

          <div className="ord-total">
            <span>Total</span>
            <span>{formatMoney(mode === 'cashier' ? netTotal : grossTotal)}</span>
          </div>

          <div className="ord-actions">
            <button className="btn-kot" onClick={sendKot} disabled={draft.length === 0}>
              Send KOT
            </button>
            {mode === 'cashier' && (
              <button
                className="btn-settle"
                onClick={openSettle}
                disabled={netTotal <= 0 || draft.length > 0}
                title={draft.length > 0 ? 'Send the pending KOT before settling' : undefined}
              >
                Settle &amp; Bill
              </button>
            )}
          </div>
        </div>
      </div>

      {pendingTx && (
        <PaymentModal
          transaction={pendingTx}
          onComplete={onSettled}
          onCancel={() => {
            // Cancelling mid equal-split keeps the shares already paid; the
            // rest stays on the table.
            setPendingTx(null);
            setEqualRun(null);
          }}
        />
      )}
    </div>
  );
};

// What is left on the table after `paid` is removed from `order` (mirrors the
// hook's removeItems, used here because the hook state is one render behind
// when chaining equal-split shares).
function removeShare(order: TableOrder | undefined, paid: Transaction): TransactionItem[] {
  if (!order) return [];
  return order.items
    .map((i) => {
      const p = paid.items.find((x) => x.productId === i.productId && x.unitPrice === i.unitPrice);
      const qty = Math.round((i.quantity - (p?.quantity ?? 0)) * 1000) / 1000;
      return { ...i, quantity: qty, total: Math.round(i.unitPrice * qty * 100) / 100 };
    })
    .filter((i) => i.quantity > 0);
}

export default OrderPanel;
